import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/StatusBadge";
import { StatusTimeline } from "../components/StatusTimeline";
import { FileUploader } from "../components/FileUploader";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Truck, FileIcon } from "lucide-react";

export default function ReturnDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [rr, setRr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/returns/${id}`); setRr(data); }
    catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (!rr) return <div>Not found.</div>;

  const totalQty = (rr.items || []).reduce((a, b) => a + (b.quantity || 0), 0);
  const totalValue = (rr.items || []).reduce((a, b) => a + (b.total_amount || 0), 0);

  const act = async (path, body) => {
    try {
      await api.post(`/returns/${id}/${path}`, body || {});
      toast.success("Updated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const canTransit = rr.status === "Submitted" && (user.role === "customer" || user.role === "admin");
  const canReceive = ["Submitted", "Material In Transit"].includes(rr.status) && (user.role === "inward" || user.role === "admin");
  const canInspect = rr.status === "Pending Inspection" && (user.role === "quality" || user.role === "admin");
  const canCreditNote = ["Approved", "Partially Approved", "Credit Note Pending"].includes(rr.status) && (user.role === "accounts" || user.role === "admin");
  const canClose = rr.status === "Credit Note Uploaded" && (user.role === "accounts" || user.role === "admin");

  return (
    <div className="space-y-6 max-w-6xl" data-testid="return-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="heading text-3xl font-bold text-slate-900">{rr.request_number}</h1>
            <StatusBadge status={rr.status} />
          </div>
          <p className="text-slate-500 mt-1">Created {new Date(rr.created_at).toLocaleString()} by {rr.created_by_email}</p>
        </div>
        <div className="flex gap-2">
          {canTransit && <Button variant="outline" onClick={() => act("transit")} data-testid="mark-transit-button"><Truck className="w-4 h-4 mr-2" />Mark in Transit</Button>}
          {canClose && <Button onClick={() => act("close")} className="bg-orange-600 hover:bg-orange-700" data-testid="close-request-button">Close Request</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer + Items */}
          <Card>
            <CardHeader><CardTitle className="heading text-base">Customer</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div><span className="text-slate-500">Name</span><div className="font-medium">{rr.customer_name}</div></div>
              <div><span className="text-slate-500">Code</span><div>{rr.customer_code}</div></div>
              <div><span className="text-slate-500">Contact</span><div>{rr.contact_person}</div></div>
              <div><span className="text-slate-500">Mobile</span><div>{rr.mobile}</div></div>
              <div><span className="text-slate-500">Email</span><div>{rr.email}</div></div>
              <div><span className="text-slate-500">Reason</span><div>{rr.reason}</div></div>
              {rr.complaint_description && <div className="col-span-2"><span className="text-slate-500">Complaint</span><div>{rr.complaint_description}</div></div>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="heading text-base">Items</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const receiptsByName = {};
                const receiptsByCode = {};
                (rr.material_receipt?.receipts || []).forEach((r) => {
                  if (r.item_name) receiptsByName[r.item_name] = r;
                  if (r.item_code) receiptsByCode[r.item_code] = r;
                });
                const hasReceipt = !!rr.material_receipt;
                return (
                  <table className="w-full text-sm">
                    <thead className="text-slate-500 border-b">
                      <tr>
                        <th className="text-left py-2">Item</th>
                        <th className="text-right">Sent Qty</th>
                        <th className="text-right">Rate</th>
                        <th className="text-right">GST%</th>
                        <th className="text-right">Total</th>
                        {hasReceipt && <th className="text-right">Received</th>}
                        {hasReceipt && <th className="text-right">Status</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rr.items.map((it, i) => {
                        const recv = receiptsByCode[it.item_code] || receiptsByName[it.item_name];
                        const received = recv?.received_qty;
                        const short = recv?.short_qty || 0;
                        const excess = recv?.excess_qty || 0;
                        let badge = null;
                        if (hasReceipt) {
                          if (excess > 0) badge = <span className="inline-block bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-0.5 text-xs font-medium">Excess +{excess}</span>;
                          else if (short > 0) badge = <span className="inline-block bg-red-50 text-red-700 border border-red-200 rounded px-2 py-0.5 text-xs font-medium">Short −{short}</span>;
                          else if (received != null) badge = <span className="inline-block bg-green-50 text-green-700 border border-green-200 rounded px-2 py-0.5 text-xs font-medium">OK</span>;
                          else badge = <span className="text-slate-400 text-xs">—</span>;
                        }
                        const rowKey = it.item_code || `${it.item_name}-${i}`;
                        return (
                          <tr key={rowKey} className="border-b last:border-0">
                            <td className="py-2">{it.item_name}</td>
                            <td className="text-right">{it.quantity} {it.unit}</td>
                            <td className="text-right">₹{it.rate}</td>
                            <td className="text-right">{it.gst_percent}</td>
                            <td className="text-right">₹{(it.total_amount || 0).toFixed(2)}</td>
                            {hasReceipt && <td className="text-right">{received != null ? `${received} ${it.unit}` : "—"}</td>}
                            {hasReceipt && <td className="text-right" data-testid={`receipt-status-${i}`}>{badge}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t font-semibold">
                        <td className="py-2">Total</td>
                        <td className="text-right">{totalQty}</td>
                        <td colSpan={2}></td>
                        <td className="text-right">₹{totalValue.toFixed(2)}</td>
                        {hasReceipt && <td colSpan={2}></td>}
                      </tr>
                    </tfoot>
                  </table>
                );
              })()}
              {rr.material_receipt && (
                <div className="mt-3 text-xs text-slate-500 border-t pt-3 flex flex-wrap gap-x-6 gap-y-1">
                  <span>Received on: <span className="text-slate-700 font-medium">{rr.material_receipt.received_date}</span></span>
                  <span>Verified by: <span className="text-slate-700 font-medium">{rr.material_receipt.received_by}</span></span>
                  {rr.material_receipt.remarks && <span>Remarks: <span className="text-slate-700">{rr.material_receipt.remarks}</span></span>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dispatch */}
          <Card>
            <CardHeader><CardTitle className="heading text-base">Dispatch</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm">
              <div><span className="text-slate-500">Transporter</span><div>{rr.transporter_name || "—"}</div></div>
              <div><span className="text-slate-500">LR/Bilty</span><div>{rr.lr_number || "—"}</div></div>
              <div><span className="text-slate-500">Dispatch Date</span><div>{rr.dispatch_date || "—"}</div></div>
              <div><span className="text-slate-500">Vehicle</span><div>{rr.vehicle_number || "—"}</div></div>
            </CardContent>
          </Card>

          {/* Attachments */}
          {rr.attachments?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="heading text-base">Attachments</CardTitle></CardHeader>
              <CardContent>
                <ul className="grid grid-cols-2 gap-2">
                  {rr.attachments.map((a, i) => (
                    <li key={a.url || `${a.filename}-${i}`}>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer"
                         className="flex items-center gap-2 border rounded p-2 hover:bg-slate-50 text-sm">
                        <FileIcon className="w-4 h-4 text-slate-400" /> <span className="truncate">{a.filename}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Credit Note — visible to all roles once issued */}
          {(rr.credit_note_number || rr.credit_note?.credit_note_number) && (
            <Card data-testid="credit-note-card" className="border-emerald-200">
              <CardHeader>
                <CardTitle className="heading text-base text-emerald-700">Credit Note</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-y-2 gap-x-6 text-sm">
                  <div>
                    <span className="text-slate-500">CN Number</span>
                    <div className="font-medium" data-testid="cn-number">
                      {rr.credit_note_number || rr.credit_note?.credit_note_number || "—"}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">CN Date</span>
                    <div className="font-medium" data-testid="cn-date">
                      {rr.credit_note_date || rr.credit_note?.credit_note_date || "—"}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-500">Amount</span>
                    <div className="font-semibold text-emerald-700" data-testid="cn-amount">
                      ₹{Number(rr.credit_note_amount ?? rr.credit_note?.credit_note_amount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  {(rr.credit_note_remarks || rr.credit_note?.remarks) && (
                    <div className="col-span-3">
                      <span className="text-slate-500">Remarks</span>
                      <div>{rr.credit_note_remarks || rr.credit_note?.remarks}</div>
                    </div>
                  )}
                </div>
                {(() => {
                  const cnAttachments = rr.credit_note_attachments || rr.credit_note?.attachments || [];
                  if (!cnAttachments.length) return null;
                  return (
                    <div className="pt-2 border-t">
                      <div className="text-sm font-medium text-slate-700 mb-2">Download Credit Note</div>
                      <ul className="grid grid-cols-2 gap-2">
                        {cnAttachments.map((a, i) => (
                          <li key={a.url || `${a.filename}-${i}`}>
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`}
                              target="_blank"
                              rel="noreferrer"
                              download={a.filename}
                              data-testid={`cn-download-${i}`}
                              className="flex items-center gap-2 border rounded p-2 hover:bg-emerald-50 text-sm"
                            >
                              <FileIcon className="w-4 h-4 text-emerald-600" />
                              <span className="truncate">{a.filename || "Credit Note"}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Role-specific action panels */}
          {canReceive && <InwardPanel rr={rr} onDone={load} />}
          {canInspect && <InspectionPanel rr={rr} onDone={load} />}
          {canCreditNote && <CreditNotePanel rr={rr} onDone={load} />}
        </div>

        <div>
          <Card>
            <CardHeader><CardTitle className="heading text-base">Status Timeline</CardTitle></CardHeader>
            <CardContent>
              <StatusTimeline history={rr.status_history} currentStatus={rr.status} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function InwardPanel({ rr, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [receipts, setReceipts] = useState(rr.items.map((it) => ({
    item_code: it.item_code, item_name: it.item_name,
    ordered_qty: it.quantity, received_qty: it.quantity, short_qty: 0, excess_qty: 0,
  })));
  const [busy, setBusy] = useState(false);
  const setR = (i, p) => setReceipts((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...p } : r)));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/inward/receipts", { return_request_id: rr.id, received_date: date, receipts, remarks, attachments });
      toast.success("Material received");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-orange-200">
      <CardHeader><CardTitle className="heading text-base text-orange-700">Inward — Material Receipt</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Date of Receipt</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="inward-date" /></div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-slate-500 border-b"><tr><th className="text-left py-2">Item</th><th className="text-right">Ordered</th><th className="text-right">Received</th><th className="text-right">Short</th><th className="text-right">Excess</th></tr></thead>
          <tbody>
            {receipts.map((r, i) => (
              <tr key={r.item_code || `${r.item_name}-${i}`} className="border-b last:border-0">
                <td className="py-2">{r.item_name}</td>
                <td className="text-right">{r.ordered_qty}</td>
                <td className="text-right"><Input type="number" className="h-8 w-24 ml-auto" value={r.received_qty} onChange={(e) => setR(i, { received_qty: parseFloat(e.target.value) || 0, short_qty: Math.max(0, r.ordered_qty - (parseFloat(e.target.value) || 0)), excess_qty: Math.max(0, (parseFloat(e.target.value) || 0) - r.ordered_qty) })} data-testid={`inward-received-${i}`} /></td>
                <td className="text-right">{r.short_qty}</td>
                <td className="text-right">{r.excess_qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        <FileUploader value={attachments} onChange={setAttachments} label="Material photos / docs" testId="inward-uploader" />
        <Button disabled={busy} className="bg-orange-600 hover:bg-orange-700" onClick={submit} data-testid="inward-submit-button">
          Confirm Receipt & Send to Quality
        </Button>
      </CardContent>
    </Card>
  );
}

function InspectionPanel({ rr, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [inspector, setInspector] = useState("");
  const [report, setReport] = useState("");
  const [remarks, setRemarks] = useState("");
  const [decision, setDecision] = useState("Approved");
  const [attachments, setAttachments] = useState([]);
  const [lineDec, setLineDec] = useState(rr.items.map((it) => ({ item_code: it.item_code, item_name: it.item_name, approved_qty: it.quantity, rejected_qty: 0, reason: "" })));
  const [busy, setBusy] = useState(false);
  const setLd = (i, p) => setLineDec((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...p } : l)));

  const submit = async () => {
    if (!inspector) return toast.error("Inspector name required");
    setBusy(true);
    try {
      await api.post("/inspection", {
        return_request_id: rr.id, inspection_date: date, inspector_name: inspector,
        report_number: report, remarks, decision, line_decisions: lineDec, attachments,
      });
      toast.success("Inspection saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-orange-200">
      <CardHeader><CardTitle className="heading text-base text-orange-700">Quality Inspection</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Inspection Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Inspector Name</Label><Input value={inspector} onChange={(e) => setInspector(e.target.value)} data-testid="inspector-name-input" /></div>
          <div><Label>Report Number</Label><Input value={report} onChange={(e) => setReport(e.target.value)} /></div>
          <div>
            <Label>Decision</Label>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid="inspection-decision"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Approved">Approve</SelectItem>
                <SelectItem value="Partially Approved">Partially Approve</SelectItem>
                <SelectItem value="Rejected">Reject</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {decision === "Partially Approved" && (
          <table className="w-full text-sm">
            <thead className="text-slate-500 border-b"><tr><th className="text-left py-2">Item</th><th>Approved Qty</th><th>Rejected Qty</th><th>Reason</th></tr></thead>
            <tbody>
              {lineDec.map((l, i) => (
                <tr key={l.item_code || `${l.item_name}-${i}`} className="border-b last:border-0">
                  <td className="py-2">{l.item_name}</td>
                  <td><Input type="number" className="h-8" value={l.approved_qty} onChange={(e) => setLd(i, { approved_qty: parseFloat(e.target.value) || 0 })} /></td>
                  <td><Input type="number" className="h-8" value={l.rejected_qty} onChange={(e) => setLd(i, { rejected_qty: parseFloat(e.target.value) || 0 })} /></td>
                  <td><Input className="h-8" value={l.reason} onChange={(e) => setLd(i, { reason: e.target.value })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        <FileUploader value={attachments} onChange={setAttachments} label="Inspection report / images (PDF, JPG, PNG)" testId="inspection-uploader" />
        <Button disabled={busy} className="bg-orange-600 hover:bg-orange-700" onClick={submit} data-testid="inspection-submit-button">
          Submit Inspection
        </Button>
      </CardContent>
    </Card>
  );
}

function CreditNotePanel({ rr, onDone }) {
  const [cn, setCn] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!cn || !amount) return toast.error("CN Number & Amount required");
    setBusy(true);
    try {
      await api.post("/credit-notes", {
        return_request_id: rr.id, credit_note_number: cn, credit_note_date: date,
        credit_note_amount: parseFloat(amount), remarks, attachments,
      });
      toast.success("Credit note uploaded");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-orange-200">
      <CardHeader><CardTitle className="heading text-base text-orange-700">Accounts — Upload Credit Note</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div><Label>CN Number</Label><Input value={cn} onChange={(e) => setCn(e.target.value)} data-testid="cn-number-input" /></div>
          <div><Label>CN Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cn-amount-input" /></div>
        </div>
        <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        <FileUploader value={attachments} onChange={setAttachments} label="Credit note PDF" testId="cn-uploader" />
        <Button disabled={busy} className="bg-orange-600 hover:bg-orange-700" onClick={submit} data-testid="cn-submit-button">Upload Credit Note</Button>
      </CardContent>
    </Card>
  );
}
